
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from '@/components/ui/use-toast';
import { enrollUserInCourse } from '@/lib/user-courses';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

const TestPaymentFlow = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const purchaseId = searchParams.get('purchaseId');
  const courseId = searchParams.get('courseId');
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Simulate Montonio's payment flow
  useEffect(() => {
    // Set initial progress
    setProgress(15);
    
    // Simulate loading bank interface
    const timer1 = setTimeout(() => {
      setProgress(35);
    }, 700);
    
    // Simulate completing loading
    const timer2 = setTimeout(() => {
      setProgress(100);
      setLoading(false);
    }, 1500);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);
  
  const handleCompletePayment = async () => {
    if (!purchaseId || !courseId || !user) return;
    
    setProcessing(true);
    
    try {
      // Update purchase status to completed
      const { error: updateError } = await supabase
        .from('purchases')
        .update({
          status: 'completed',
          payment_status: 'completed',
          payment_completed_at: new Date().toISOString()
        })
        .eq('id', purchaseId);
        
      if (updateError) {
        throw new Error(updateError.message);
      }
      
      // Enroll user in the course
      const enrollment = await enrollUserInCourse(user.id, courseId);
      
      if (!enrollment) {
        throw new Error("Failed to enroll in course");
      }
      
      // Show success message
      toast({
        title: "Makse õnnestus!",
        description: "Oled kursusele registreeritud.",
      });
      
      // Redirect to success page
      navigate(`/checkout-success?purchaseId=${purchaseId}&courseId=${courseId}`);
      
    } catch (error) {
      console.error("Test payment error:", error);
      toast({
        title: "Viga",
        description: "Makse protsessimisel tekkis viga. Palun proovi uuesti.",
        variant: "destructive"
      });
      setProcessing(false);
    }
  };
  
  const handleCancelPayment = () => {
    // Return to checkout page
    if (courseId) {
      navigate(`/checkout?courseId=${courseId}`);
    } else {
      navigate('/');
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-lg">
        <CardHeader className="bg-blue-50 border-b border-blue-100">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-xl text-blue-800">Test Bank</CardTitle>
              <CardDescription>Test Mode Payment Flow</CardDescription>
            </div>
            <div className="bg-white p-2 rounded-md">
              <img 
                src="https://placehold.co/100x40/e8f4ff/0066cc?text=TestBank" 
                alt="Bank logo" 
                className="h-8"
              />
            </div>
          </div>
        </CardHeader>
        
        {loading ? (
          <CardContent className="pt-6 pb-8">
            <div className="text-center mb-6">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Connecting to banking system...</p>
            </div>
            <Progress value={progress} className="h-2" />
          </CardContent>
        ) : (
          <>
            <CardContent className="pt-6">
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <p className="text-yellow-800 font-medium">This is a test payment simulation</p>
                <p className="text-yellow-700 text-sm mt-1">
                  In a real environment, you would be redirected to your actual bank.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-600">Merchant:</span>
                  <span className="font-medium">Kaija OÜ</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-medium">€{(searchParams.get('amount') || '0')}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-600">Reference:</span>
                  <span className="font-medium text-sm">{purchaseId || 'Unknown'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Description:</span>
                  <span className="font-medium">Course purchase</span>
                </div>
              </div>
            </CardContent>
            
            <CardFooter className="flex flex-col space-y-3 pt-6">
              <Button 
                onClick={handleCompletePayment}
                disabled={processing}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : "Complete Test Payment"}
              </Button>
              
              <Button 
                variant="outline"
                onClick={handleCancelPayment}
                disabled={processing}
                className="w-full"
              >
                Cancel Payment
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
};

export default TestPaymentFlow;
